import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../supabase/AuthContext';
import { syncAll } from './syncService';

export type SyncStatus = 'idle' | 'syncing' | 'error';

export interface UseSyncReturn {
  status: SyncStatus;
  lastSyncAt: Date | null;
  lastError: string | null;
  triggerSync: () => void;
}

const SYNC_INTERVAL_MS = 5 * 60 * 1000;

export function useSync(): UseSyncReturn {
  const { session, isConfigured } = useAuth();
  const [status, setStatus] = useState<SyncStatus>('idle');
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  // Pin the latest user id in a ref so the interval callback always sees the
  // most recent value without forcing the interval to re-create on every
  // session change.
  const userIdRef = useRef<string | null>(session?.user.id ?? null);
  useEffect(() => {
    userIdRef.current = session?.user.id ?? null;
  }, [session?.user.id]);

  // Guard against overlapping runs (e.g. interval fires while a manual
  // refresh is still pending).
  const runningRef = useRef(false);

  // `runSync` and `triggerSync` are explicitly memoized with `useCallback`.
  // The React Compiler can't reliably preserve their identity here because
  // `runSync` is named in the polling-effect's dep array below; without a
  // stable reference the effect re-runs every render, kicks off a fresh
  // `runSync()`, which setStates, which re-renders — a render loop.
  // Verified in tests: removing the wrappers reintroduces the loop even
  // with the compiler enabled. Keep them.
  const runSync = useCallback(async () => {
    if (runningRef.current) return;
    const userId = userIdRef.current;
    if (!isConfigured || !userId) return;
    runningRef.current = true;
    setStatus('syncing');
    try {
      const result = await syncAll(userId);
      setLastSyncAt(new Date());
      if (result.errors.length > 0) {
        setStatus('error');
        setLastError(result.errors[0] ?? 'Unknown sync error');
      } else {
        setStatus('idle');
        setLastError(null);
      }
    } catch (error) {
      setStatus('error');
      setLastError(error instanceof Error ? error.message : 'Sync failed');
    } finally {
      runningRef.current = false;
    }
  }, [isConfigured]);

  const triggerSync = useCallback(() => {
    void runSync();
  }, [runSync]);

  // Sync on session becoming available + every 5 minutes thereafter. Depend on
  // a derived boolean rather than `session` itself: Supabase mints a fresh
  // session object on every silent token refresh, and depending on that ref
  // would tear down and re-create the 5-minute interval on each refresh
  // (~hourly), defeating the cadence. `userIdRef` already pins the latest
  // user id for `runSync` to read.
  const hasSession = session !== null;
  useEffect(() => {
    if (!isConfigured || !hasSession) return;
    void runSync();
    const id = window.setInterval(() => {
      void runSync();
    }, SYNC_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [isConfigured, hasSession, runSync]);

  return { status, lastSyncAt, lastError, triggerSync };
}
